/**
 * TavernBackdrop — UI-2 four-layer parallax tavern interior, mounted
 * behind the Room when the lobby is in a non-playing state. Renders:
 *
 *   layer 0 — far wall + hearth with a flickering ember glow
 *   layer 1 — mid-shelf silhouettes (mugs, lantern hooks, bottles)
 *   layer 2 — near table edge + walking-floor strip
 *   layer 3 — ambient dust motes (canvas-driven, sparse)
 *
 * Time-of-day: dusk → night → late night via Date.now() % cycleMs.
 * NPC pass-through: every ~90s a silhouette walks across the back.
 * Reduce-motion: static frame, no canvas, no NPC, no flicker.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '@tt/motion';

interface Props {
  /** Show the backdrop only when the lobby is in these states. */
  active: boolean;
}

const TIME_CYCLE_MS = 1000 * 60 * 18; // ~18-min full cycle (dusk → late)
const NPC_INTERVAL_MS = 90_000;

export function TavernBackdrop({ active }: Props) {
  const reduce = prefersReducedMotion();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [npcSeed, setNpcSeed] = useState<{ id: number; kind: NpcKind } | null>(null);
  const [tod, setTod] = useState(timeOfDay());

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const tick = () => { setTod(timeOfDay()); raf = window.requestAnimationFrame(tick); };
    if (!reduce) raf = window.requestAnimationFrame(tick);
    return () => { if (raf) window.cancelAnimationFrame(raf); };
  }, [active, reduce]);

  // Cycle NPCs every NPC_INTERVAL_MS.
  useEffect(() => {
    if (!active || reduce) return;
    let id = 0;
    const fire = () => {
      const kinds: NpcKind[] = ['keeper', 'cat', 'courier', 'spirit'];
      const kind = kinds[Math.floor(Math.random() * kinds.length)] ?? 'cat';
      id += 1;
      setNpcSeed({ id, kind });
    };
    fire();
    const handle = window.setInterval(fire, NPC_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [active, reduce]);

  // Dust motes — light canvas overlay.
  useEffect(() => {
    if (!active || reduce) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio ?? 1);
    const sync = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    sync();
    window.addEventListener('resize', sync);
    interface Mote { x: number; y: number; vx: number; vy: number; life: number; max: number; r: number }
    const motes: Mote[] = [];
    const targetCount = 60;
    function spawn(): Mote {
      return {
        x: Math.random() * canvas!.width,
        y: Math.random() * canvas!.height,
        vx: (Math.random() - 0.5) * 0.18 * dpr,
        vy: -Math.random() * 0.22 * dpr,
        life: 0,
        max: 4500 + Math.random() * 6000,
        r: (1 + Math.random() * 1.6) * dpr,
      };
    }
    let last = performance.now();
    let raf = 0;
    const draw = (now: number) => {
      const dt = Math.min(48, now - last);
      last = now;
      while (motes.length < targetCount) motes.push(spawn());
      ctx.clearRect(0, 0, canvas!.width, canvas!.height);
      for (const m of motes) {
        m.life += dt;
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        const a = Math.max(0, Math.min(1, 1 - Math.abs((m.life / m.max) - 0.5) * 2)) * 0.85;
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 210, 130, ${a})`;
        ctx.shadowColor = 'rgba(255, 200, 100, 0.7)';
        ctx.shadowBlur = 6;
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      for (let i = motes.length - 1; i >= 0; i--) {
        if (motes[i]!.life > motes[i]!.max || motes[i]!.y < -10) motes.splice(i, 1);
      }
      raf = window.requestAnimationFrame(draw);
    };
    raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); window.removeEventListener('resize', sync); };
  }, [active, reduce]);

  if (!active) return null;
  return (
    <div className={`tavern-backdrop tavern-tod-${tod}`} aria-hidden="true">
      <TavernInteriorSvg />
      {!reduce && <canvas ref={canvasRef} className="tavern-motes" />}
      {!reduce && npcSeed && <NpcWalker key={npcSeed.id} kind={npcSeed.kind} />}
    </div>
  );
}

/**
 * Full waiting-room tavern interior — wide-format SVG (1600×900) per
 * docs/ui/room-screens.md §9. Stone wall + plank wainscoting +
 * perspective floor + central hearth + right-side bar with bottles +
 * 4 hanging lanterns + 4 wall sconces + pendulum clock + dartboard +
 * mid-distance stools + framed picture. All ambient animation via
 * SVG SMIL `<animate>`; reduced-motion freezes flames + pendulum.
 *
 * 2026-05-16 quality pass: per-plank wood grain (knots + striations
 * via filters), volumetric hearth light cone, brick-textured stone
 * mortar, detailed bottles with reflections + cork tops, brass
 * hardware on lanterns + sconces, hearth grate + log silhouettes,
 * cobweb in corner, soft warm light beams from hanging lanterns.
 */
function TavernInteriorSvg() {
  return (
    <svg
      className="tavern-svg"
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="tvSky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor="#1a0e1f" />
          <stop offset="40%" stopColor="#2a1a2a" />
          <stop offset="78%" stopColor="#3a2418" />
          <stop offset="100%" stopColor="#1a0e08" />
        </linearGradient>

        {/* Stone wall — varied stone sizes, two-tone mortar, occasional
         *  chipped corners + a moss tint near the floor edge. The chips
         *  + dark mortar make the surface read as hand-laid masonry
         *  rather than a tiled pattern. */}
        <pattern id="tvStone" x="0" y="0" width="160" height="80" patternUnits="userSpaceOnUse">
          <rect width="160" height="80" fill="#1a141c" />
          {/* Mortar shadow base */}
          <rect width="160" height="80" fill="#0a0810" opacity="0.35" />
          {/* Row 1 — 3 large stones */}
          <rect x="2"   y="2"  width="60" height="34" fill="#322438" stroke="#070510" strokeWidth="2" rx="2" />
          <rect x="66"  y="2"  width="44" height="34" fill="#2a1e30" stroke="#070510" strokeWidth="2" rx="2" />
          <rect x="114" y="2"  width="44" height="34" fill="#352840" stroke="#070510" strokeWidth="2" rx="2" />
          {/* Chip on stone 1 corner */}
          <path d="M 2 30 L 12 34 L 6 36 Z" fill="#0a0810" opacity="0.7" />
          {/* Highlight glints */}
          <line x1="6"   y1="6"  x2="58"  y2="6"  stroke="#4a3a55" strokeWidth="0.6" opacity="0.55" />
          <line x1="70"  y1="6"  x2="106" y2="6"  stroke="#3a2c45" strokeWidth="0.6" opacity="0.55" />
          {/* Row 2 — offset 4 stones */}
          <rect x="-28" y="42" width="60" height="36" fill="#2c2034" stroke="#070510" strokeWidth="2" rx="2" />
          <rect x="36"  y="42" width="50" height="36" fill="#382a48" stroke="#070510" strokeWidth="2" rx="2" />
          <rect x="90"  y="42" width="38" height="36" fill="#241828" stroke="#070510" strokeWidth="2" rx="2" />
          <rect x="132" y="42" width="60" height="36" fill="#2e2238" stroke="#070510" strokeWidth="2" rx="2" />
          {/* Stone speckle (random light pinpricks) */}
          <circle cx="28"  cy="18" r="0.8" fill="#5a4a65" opacity="0.6" />
          <circle cx="86"  cy="20" r="0.8" fill="#5a4a65" opacity="0.5" />
          <circle cx="132" cy="14" r="0.8" fill="#5a4a65" opacity="0.55" />
          <circle cx="50"  cy="58" r="0.8" fill="#5a4a65" opacity="0.5" />
          <circle cx="108" cy="62" r="0.8" fill="#5a4a65" opacity="0.55" />
          <circle cx="166" cy="56" r="0.8" fill="#5a4a65" opacity="0.5" />
        </pattern>

        {/* Plank wall — per-plank color variation, end-grain caps top
         *  and bottom, knots scattered through the boards, and faint
         *  vertical grain striations for texture. Pattern width = 4
         *  planks wide so the variation doesn't read as repetition. */}
        <pattern id="tvPlankWall" x="0" y="0" width="128" height="240" patternUnits="userSpaceOnUse">
          <rect width="128" height="240" fill="#1a0e08" />
          {/* 4 planks, each 32 wide, alternating tones + a couple of variants */}
          <rect x="0"   y="0" width="32" height="240" fill="#3a2614" />
          <rect x="32"  y="0" width="32" height="240" fill="#2a1810" />
          <rect x="64"  y="0" width="32" height="240" fill="#322012" />
          <rect x="96"  y="0" width="32" height="240" fill="#26160c" />
          {/* Plank seams */}
          <line x1="0"   y1="0" x2="0"   y2="240" stroke="#080402" strokeWidth="1.4" />
          <line x1="32"  y1="0" x2="32"  y2="240" stroke="#080402" strokeWidth="1.4" />
          <line x1="64"  y1="0" x2="64"  y2="240" stroke="#080402" strokeWidth="1.4" />
          <line x1="96"  y1="0" x2="96"  y2="240" stroke="#080402" strokeWidth="1.4" />
          {/* Bevel highlight on left edge of each plank */}
          <line x1="2"   y1="0" x2="2"   y2="240" stroke="#5b3a1f" strokeWidth="0.6" opacity="0.55" />
          <line x1="34"  y1="0" x2="34"  y2="240" stroke="#4a2e18" strokeWidth="0.6" opacity="0.45" />
          <line x1="66"  y1="0" x2="66"  y2="240" stroke="#5b3a1f" strokeWidth="0.6" opacity="0.5" />
          <line x1="98"  y1="0" x2="98"  y2="240" stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
          {/* Subtle grain striations */}
          <line x1="14"  y1="0" x2="14"  y2="240" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
          <line x1="22"  y1="0" x2="22"  y2="240" stroke="#1a0e08" strokeWidth="0.3" opacity="0.4" />
          <line x1="48"  y1="0" x2="48"  y2="240" stroke="#1a0e08" strokeWidth="0.4" opacity="0.55" />
          <line x1="78"  y1="0" x2="78"  y2="240" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
          <line x1="110" y1="0" x2="110" y2="240" stroke="#1a0e08" strokeWidth="0.4" opacity="0.5" />
          {/* Knots — small dark filled circles with concentric rings */}
          <g opacity="0.85">
            <circle cx="16"  cy="62"  r="3.5" fill="#0a0604" />
            <ellipse cx="16" cy="62"  rx="6"  ry="2.5" fill="none" stroke="#1a0e08" strokeWidth="0.6" />
            <circle cx="80"  cy="148" r="3"   fill="#0a0604" />
            <ellipse cx="80" cy="148" rx="5.5" ry="2" fill="none" stroke="#1a0e08" strokeWidth="0.6" />
            <circle cx="48"  cy="198" r="2.5" fill="#0a0604" />
            <ellipse cx="48" cy="198" rx="4.5" ry="1.6" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
            <circle cx="108" cy="38"  r="2.8" fill="#0a0604" />
            <ellipse cx="108" cy="38" rx="5"  ry="1.8" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
          </g>
        </pattern>

        {/* Floor planks — running PARALLEL to the camera (horizontal)
         *  with per-plank tonal variation, knots, and a darker overlay
         *  fading from the back to add perspective. */}
        <pattern id="tvPlankFloor" x="0" y="0" width="240" height="112" patternUnits="userSpaceOnUse">
          <rect width="240" height="112" fill="#1a0e08" />
          {/* 4 plank rows, each 28 tall */}
          <rect x="0" y="0"  width="240" height="28" fill="#3a2410" />
          <rect x="0" y="28" width="240" height="28" fill="#28180e" />
          <rect x="0" y="56" width="240" height="28" fill="#322012" />
          <rect x="0" y="84" width="240" height="28" fill="#26170c" />
          {/* Plank seams */}
          <line x1="0" y1="0"   x2="240" y2="0"   stroke="#080402" strokeWidth="1.4" />
          <line x1="0" y1="28"  x2="240" y2="28"  stroke="#080402" strokeWidth="1.4" />
          <line x1="0" y1="56"  x2="240" y2="56"  stroke="#080402" strokeWidth="1.4" />
          <line x1="0" y1="84"  x2="240" y2="84"  stroke="#080402" strokeWidth="1.4" />
          {/* Edge highlights (top of each plank) */}
          <line x1="0" y1="2"   x2="240" y2="2"   stroke="#5b3a1f" strokeWidth="0.6" opacity="0.45" />
          <line x1="0" y1="30"  x2="240" y2="30"  stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
          <line x1="0" y1="58"  x2="240" y2="58"  stroke="#5b3a1f" strokeWidth="0.6" opacity="0.45" />
          <line x1="0" y1="86"  x2="240" y2="86"  stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
          {/* End-cap board breaks (vertical at random x — staggered) */}
          <line x1="78"  y1="0"  x2="78"  y2="28"  stroke="#080402" strokeWidth="1" />
          <line x1="162" y1="28" x2="162" y2="56"  stroke="#080402" strokeWidth="1" />
          <line x1="48"  y1="56" x2="48"  y2="84"  stroke="#080402" strokeWidth="1" />
          <line x1="180" y1="84" x2="180" y2="112" stroke="#080402" strokeWidth="1" />
          {/* Knots */}
          <g opacity="0.8">
            <circle cx="50"  cy="14" r="2.5" fill="#0a0604" />
            <ellipse cx="50" cy="14" rx="4.5" ry="1.6" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
            <circle cx="190" cy="42" r="2" fill="#0a0604" />
            <ellipse cx="190" cy="42" rx="3.5" ry="1.4" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
            <circle cx="106" cy="70" r="2.4" fill="#0a0604" />
            <circle cx="220" cy="98" r="2" fill="#0a0604" />
          </g>
        </pattern>

        <radialGradient id="tvHearthGlow" cx="0.5" cy="0.7" r="0.7">
          <stop offset="0%"   stopColor="#ffd28a" stopOpacity="0.9" />
          <stop offset="40%"  stopColor="#ff8c42" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ff4a2a" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvHearthHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ff8c42" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#c25516" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvLanternHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.36" />
          <stop offset="100%" stopColor="#ff8c42" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="tvSconceHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.26" />
          <stop offset="100%" stopColor="#ff8c42" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="tvBottleG" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2a4a2a" /><stop offset="100%" stopColor="#0a1a0a" />
        </linearGradient>
        <linearGradient id="tvBottleA" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#a87a3a" /><stop offset="100%" stopColor="#3a2410" />
        </linearGradient>
        <linearGradient id="tvBottleC" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5a4a4a" /><stop offset="100%" stopColor="#1a0e0a" />
        </linearGradient>
        <linearGradient id="tvOak" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#5b3a1f" /><stop offset="60%" stopColor="#3a2410" /><stop offset="100%" stopColor="#1a0e08" />
        </linearGradient>
        <linearGradient id="tvTopFog" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="rgba(10,8,20,0.85)" />
          <stop offset="100%" stopColor="rgba(10,8,20,0)" />
        </linearGradient>

        <style>
          {`@media (prefers-reduced-motion: reduce) {
            .tavern-svg animate, .tavern-svg animateTransform { display: none; }
            .tavern-svg .tv-flame-core { opacity: 0.85 !important; }
            .tavern-svg .tv-flame-mid  { opacity: 0.7 !important; }
            .tavern-svg .tv-flame-out  { opacity: 0.5 !important; }
          }`}
        </style>
      </defs>

      {/* sky/wall */}
      <rect x="0" y="0" width="1600" height="900" fill="url(#tvSky)" />
      {/* stone wall back */}
      <rect x="0" y="0" width="1600" height="500" fill="url(#tvStone)" />
      <rect x="0" y="0" width="1600" height="500" fill="rgba(0,0,0,0.18)" />
      {/* plank wainscoting */}
      <rect x="0" y="380" width="1600" height="240" fill="url(#tvPlankWall)" />
      <rect x="0" y="376" width="1600" height="8" fill="#5b3a1f" />
      <rect x="0" y="374" width="1600" height="2" fill="#7a4a28" />
      <rect x="0" y="612" width="1600" height="6" fill="#3a2410" />
      <rect x="0" y="610" width="1600" height="2" fill="#5b3a1f" />

      {/* floor with perspective hint */}
      <rect x="0" y="620" width="1600" height="280" fill="url(#tvPlankFloor)" />
      <rect x="0" y="620" width="1600" height="40" fill="rgba(0,0,0,0.35)" />
      <rect x="0" y="660" width="1600" height="32" fill="rgba(0,0,0,0.18)" />
      <rect x="0" y="692" width="1600" height="22" fill="rgba(0,0,0,0.08)" />

      {/* HEARTH (center) — brick mantel + iron grate + log silhouettes
          + ember pile + flames. Opacities toned so the UI overlays read
          cleanly on top. */}
      <g className="tv-hearth" opacity="0.6">
        {/* Volumetric light cone — soft warm trapezoid spreading from
            the firebox out onto the floor. */}
        <path d="M 730 540 L 870 540 L 1010 760 L 590 760 Z" fill="url(#tvHearthHalo)" opacity="0.32" />
        <ellipse cx="800" cy="720" rx="280" ry="80" fill="url(#tvHearthHalo)" opacity="0.55">
          <animate attributeName="rx" values="280;310;290;280" dur="4s" repeatCount="indefinite" />
        </ellipse>

        {/* Brick stone surround — outer carved arch */}
        <path d="M 690 620 L 690 460 Q 690 320 800 320 Q 910 320 910 460 L 910 620 Z" fill="#1a1218" stroke="#0a0608" strokeWidth="3" />
        {/* Brick texture on the arch face (8 stones around the arch) */}
        <g opacity="0.85" stroke="#0a0608" strokeWidth="1.2">
          <path d="M 690 620 L 690 540 L 706 540 L 706 620 Z" fill="#2a1f2a" />
          <path d="M 690 540 L 690 460 L 706 462 L 706 540 Z" fill="#231828" />
          <path d="M 690 460 Q 696 380 740 340 L 752 354 Q 712 388 706 462 Z" fill="#2a1f2a" />
          <path d="M 740 340 Q 770 320 800 320 L 800 336 Q 772 336 752 354 Z" fill="#251a2a" />
          <path d="M 800 320 Q 830 320 860 340 L 848 354 Q 828 336 800 336 Z" fill="#2a1f2a" />
          <path d="M 860 340 Q 904 380 910 460 L 894 462 Q 888 388 848 354 Z" fill="#251a2a" />
          <path d="M 910 460 L 910 540 L 894 540 L 894 462 Z" fill="#2a1f2a" />
          <path d="M 910 540 L 910 620 L 894 620 L 894 540 Z" fill="#231828" />
        </g>

        {/* Wood beam mantel above the firebox (with grain) */}
        <rect x="668" y="304" width="264" height="22" fill="#3a2410" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="664" y="298" width="272" height="8"  fill="#5b3a1f" />
        <line x1="668" y1="312" x2="932" y2="312" stroke="#5b3a1f" strokeWidth="0.5" opacity="0.5" />
        <line x1="668" y1="318" x2="932" y2="318" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
        {/* Decorative corbels under the mantel */}
        <path d="M 670 326 L 700 326 L 690 350 L 678 350 Z" fill="#2a1810" stroke="#0a0604" strokeWidth="1" />
        <path d="M 900 326 L 930 326 L 922 350 L 910 350 Z" fill="#2a1810" stroke="#0a0604" strokeWidth="1" />

        {/* Inner firebox (deep shadow) */}
        <rect x="730" y="430" width="140" height="190" fill="#080406" />
        {/* Iron grate bars (vertical) */}
        <g opacity="0.85" stroke="#1a1218" strokeWidth="1.5">
          <line x1="744" y1="578" x2="744" y2="618" />
          <line x1="762" y1="578" x2="762" y2="618" />
          <line x1="780" y1="578" x2="780" y2="618" />
          <line x1="798" y1="578" x2="798" y2="618" />
          <line x1="816" y1="578" x2="816" y2="618" />
          <line x1="834" y1="578" x2="834" y2="618" />
          <line x1="852" y1="578" x2="852" y2="618" />
        </g>
        <line x1="734" y1="578" x2="866" y2="578" stroke="#1a1218" strokeWidth="2" />

        {/* Log silhouettes (back row, slightly visible against fire) */}
        <ellipse cx="770" cy="600" rx="32" ry="9" fill="#3a2410" />
        <ellipse cx="830" cy="600" rx="32" ry="9" fill="#3a2410" />
        <ellipse cx="800" cy="588" rx="38" ry="9" fill="#5b3a1f" />
        {/* Log end-grain rings */}
        <ellipse cx="770" cy="600" rx="6" ry="3" fill="#1a0e08" opacity="0.7" />
        <ellipse cx="830" cy="600" rx="6" ry="3" fill="#1a0e08" opacity="0.7" />
        <ellipse cx="800" cy="588" rx="7" ry="3" fill="#1a0e08" opacity="0.7" />

        {/* Ember bed (glowing rocks beneath logs) */}
        <ellipse cx="800" cy="595" rx="44" ry="9" fill="#ff4a2a" opacity="0.45">
          <animate attributeName="opacity" values="0.4;0.6;0.45" dur="1.7s" repeatCount="indefinite" />
        </ellipse>
        {/* Individual hot embers */}
        <circle cx="770" cy="608" r="1.8" fill="#ffd28a" opacity="0.85">
          <animate attributeName="opacity" values="0.6;1;0.7" dur="1.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="800" cy="612" r="2" fill="#ffd28a" opacity="0.9">
          <animate attributeName="opacity" values="0.7;1;0.8" dur="1.1s" repeatCount="indefinite" />
        </circle>
        <circle cx="828" cy="606" r="1.6" fill="#ffd28a" opacity="0.85">
          <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.6s" repeatCount="indefinite" />
        </circle>

        {/* Flame layers — outer wash, mid envelope, hot core */}
        <ellipse className="tv-flame-out" cx="800" cy="540" rx="48" ry="70" fill="#ff8c42" opacity="0.32">
          <animate attributeName="opacity" values="0.26;0.42;0.32" dur="1.3s" repeatCount="indefinite" />
        </ellipse>
        <ellipse className="tv-flame-mid" cx="800" cy="540" rx="30" ry="58" fill="#ffb27a" opacity="0.45">
          <animate attributeName="opacity" values="0.38;0.58;0.45" dur="0.9s" repeatCount="indefinite" />
        </ellipse>
        <ellipse className="tv-flame-core" cx="800" cy="548" rx="16" ry="42" fill="#fff7c2" opacity="0.55">
          <animate attributeName="opacity" values="0.45;0.7;0.55;0.5" dur="0.6s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="800" cy="540" rx="60" ry="80" fill="url(#tvHearthGlow)" opacity="0.6" />

        {/* Smoke wisps rising above the flame */}
        <g opacity="0.18">
          <ellipse cx="794" cy="460" rx="10" ry="14" fill="#5a4a4a">
            <animate attributeName="opacity" values="0;0.18;0" dur="3.8s" repeatCount="indefinite" />
            <animate attributeName="cy" values="460;420;380" dur="3.8s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="806" cy="450" rx="9" ry="12" fill="#5a4a4a">
            <animate attributeName="opacity" values="0;0.16;0" dur="4.6s" begin="1.2s" repeatCount="indefinite" />
            <animate attributeName="cy" values="450;410;360" dur="4.6s" begin="1.2s" repeatCount="indefinite" />
          </ellipse>
        </g>

        {/* Cast-iron hook over the firebox (kitchen-style detail) */}
        <line x1="800" y1="326" x2="800" y2="370" stroke="#0a0608" strokeWidth="2" />
        <path d="M 800 370 Q 800 380 808 380 Q 816 380 816 372" fill="none" stroke="#0a0608" strokeWidth="2" />
      </g>

      {/* BAR (right side) — shelved bottles with cork tops, paper
       *  labels, light reflections; iron-hooped cask; bar counter
       *  with grain; tankards with handles + foam; lit candle. */}
      <g className="tv-bar">
        {/* Shelf back (deep recess) */}
        <rect x="1280" y="380" width="320" height="220" fill="#1a0c08" stroke="#0a0604" strokeWidth="2" />
        {/* Shelf wood inserts */}
        <rect x="1280" y="380" width="320" height="220" fill="#2a1a10" opacity="0.85" />
        {/* Shelf rails (slight bevel) */}
        <line x1="1280" y1="450" x2="1600" y2="450" stroke="#5b3a1f" strokeWidth="2" />
        <line x1="1280" y1="452" x2="1600" y2="452" stroke="#1a0e08" strokeWidth="1" />
        <line x1="1280" y1="520" x2="1600" y2="520" stroke="#5b3a1f" strokeWidth="2" />
        <line x1="1280" y1="522" x2="1600" y2="522" stroke="#1a0e08" strokeWidth="1" />

        {/* Row 1 bottles — detailed (neck + body + cork + label glint) */}
        {[
          { x: 1296, h: 54, color: 'tvBottleG' as const, label: true },
          { x: 1320, h: 50, color: 'tvBottleA' as const, label: false },
          { x: 1344, h: 54, color: 'tvBottleC' as const, label: true },
          { x: 1368, h: 48, color: 'tvBottleG' as const, label: false },
          { x: 1392, h: 54, color: 'tvBottleA' as const, label: true },
          { x: 1416, h: 50, color: 'tvBottleC' as const, label: false },
          { x: 1440, h: 54, color: 'tvBottleG' as const, label: true },
          { x: 1464, h: 50, color: 'tvBottleA' as const, label: false },
          { x: 1488, h: 54, color: 'tvBottleC' as const, label: false },
          { x: 1512, h: 50, color: 'tvBottleG' as const, label: true },
          { x: 1536, h: 54, color: 'tvBottleA' as const, label: false },
          { x: 1560, h: 50, color: 'tvBottleC' as const, label: true },
          { x: 1584, h: 54, color: 'tvBottleG' as const, label: false },
        ].map((b, i) => {
          const bodyY = 396 + (54 - b.h);
          const neckY = bodyY - 8;
          return (
            <g key={`tb-r1-${i}`}>
              {/* Neck */}
              <rect x={b.x + 4} y={neckY} width="6" height="10" fill={`url(#${b.color})`} />
              {/* Cork */}
              <rect x={b.x + 3} y={neckY - 4} width="8" height="5" fill="#a87a3a" stroke="#1a0e08" strokeWidth="0.5" />
              {/* Body */}
              <rect x={b.x} y={bodyY} width="14" height={b.h} fill={`url(#${b.color})`} rx="1.5" />
              {/* Label (small parchment band) */}
              {b.label && (
                <>
                  <rect x={b.x + 2} y={bodyY + b.h * 0.45} width="10" height="10" fill="#f3e3c2" opacity="0.7" />
                  <line x1={b.x + 4} y1={bodyY + b.h * 0.48} x2={b.x + 10} y2={bodyY + b.h * 0.48} stroke="#3a2410" strokeWidth="0.6" />
                  <line x1={b.x + 4} y1={bodyY + b.h * 0.52} x2={b.x + 10} y2={bodyY + b.h * 0.52} stroke="#3a2410" strokeWidth="0.6" />
                </>
              )}
              {/* Reflection glint on left edge */}
              <rect x={b.x + 1} y={bodyY + 4} width="1.5" height={b.h - 12} fill="#ffffff" opacity="0.18" />
            </g>
          );
        })}

        {/* Row 2 bottles */}
        {[
          { x: 1296, h: 52, color: 'tvBottleA' as const },
          { x: 1320, h: 48, color: 'tvBottleG' as const },
          { x: 1344, h: 52, color: 'tvBottleC' as const },
          { x: 1368, h: 48, color: 'tvBottleA' as const },
          { x: 1392, h: 52, color: 'tvBottleG' as const },
          { x: 1416, h: 48, color: 'tvBottleC' as const },
          { x: 1440, h: 52, color: 'tvBottleA' as const },
          { x: 1464, h: 48, color: 'tvBottleG' as const },
          { x: 1488, h: 52, color: 'tvBottleC' as const },
          { x: 1512, h: 48, color: 'tvBottleA' as const },
          { x: 1536, h: 52, color: 'tvBottleG' as const },
          { x: 1560, h: 48, color: 'tvBottleC' as const },
          { x: 1584, h: 52, color: 'tvBottleA' as const },
        ].map((b, i) => {
          const bodyY = 466 + (52 - b.h);
          return (
            <g key={`tb-r2-${i}`}>
              <rect x={b.x + 4} y={bodyY - 8} width="6" height="10" fill={`url(#${b.color})`} />
              <rect x={b.x + 3} y={bodyY - 12} width="8" height="5" fill="#a87a3a" stroke="#1a0e08" strokeWidth="0.5" />
              <rect x={b.x} y={bodyY} width="14" height={b.h} fill={`url(#${b.color})`} rx="1.5" />
              <rect x={b.x + 1} y={bodyY + 4} width="1.5" height={b.h - 12} fill="#ffffff" opacity="0.16" />
            </g>
          );
        })}

        {/* Cask — barrel staves + iron hoops */}
        <g>
          <rect x="1380" y="538" width="80" height="58" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
          <ellipse cx="1420" cy="567" rx="40" ry="29" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
          {/* Stave seams */}
          <line x1="1395" y1="540" x2="1395" y2="594" stroke="#3a2410" strokeWidth="0.6" opacity="0.7" />
          <line x1="1410" y1="540" x2="1410" y2="594" stroke="#3a2410" strokeWidth="0.6" opacity="0.7" />
          <line x1="1430" y1="540" x2="1430" y2="594" stroke="#3a2410" strokeWidth="0.6" opacity="0.7" />
          <line x1="1445" y1="540" x2="1445" y2="594" stroke="#3a2410" strokeWidth="0.6" opacity="0.7" />
          {/* Iron hoops (top + bottom + middle) */}
          <rect x="1378" y="544" width="84" height="4" fill="#3a2c30" stroke="#0a0608" strokeWidth="0.6" />
          <rect x="1378" y="578" width="84" height="4" fill="#3a2c30" stroke="#0a0608" strokeWidth="0.6" />
          <rect x="1378" y="562" width="84" height="3" fill="#3a2c30" stroke="#0a0608" strokeWidth="0.4" />
          {/* Bung tap */}
          <rect x="1418" y="583" width="6" height="8" fill="#1a0e08" />
          <rect x="1420" y="591" width="2" height="4" fill="#3a2410" />
        </g>

        {/* Bar counter */}
        <rect x="1240" y="600" width="360" height="22" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="2" />
        <rect x="1240" y="600" width="360" height="3"  fill="#a87a3a" opacity="0.7" />
        {/* Counter wood grain */}
        <line x1="1240" y1="608" x2="1600" y2="608" stroke="#3a2410" strokeWidth="0.5" opacity="0.5" />
        <line x1="1240" y1="615" x2="1600" y2="615" stroke="#1a0e08" strokeWidth="0.5" opacity="0.6" />
        <rect x="1240" y="622" width="360" height="200" fill="url(#tvOak)" stroke="#1a0e08" strokeWidth="2" />
        {/* Bar front panel inset */}
        <rect x="1264" y="638" width="312" height="160" fill="none" stroke="#1a0e08" strokeWidth="1" opacity="0.6" />
        <rect x="1240" y="800" width="360" height="22"  fill="#1a0e08" />

        {/* Tankards — pewter with handle + foam top */}
        <g>
          <rect x="1280" y="572" width="26" height="28" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="1293" cy="572" rx="13" ry="3" fill="#f3e3c2" opacity="0.9" />
          <ellipse cx="1293" cy="570" rx="11" ry="2" fill="#fff7c2" opacity="0.7" />
          <path d="M 1306 576 Q 1314 576 1314 586 Q 1314 596 1306 596" fill="none" stroke="#1a0e08" strokeWidth="1.5" />
          {/* Tankard rim highlight */}
          <line x1="1282" y1="576" x2="1304" y2="576" stroke="#7a5828" strokeWidth="0.6" opacity="0.7" />
        </g>
        <g>
          <rect x="1360" y="574" width="22" height="26" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="1371" cy="574" rx="11" ry="2.5" fill="#f3e3c2" opacity="0.85" />
          <path d="M 1382 578 Q 1389 578 1389 587 Q 1389 596 1382 596" fill="none" stroke="#1a0e08" strokeWidth="1.5" />
        </g>

        {/* Bar candle in brass holder */}
        <rect x="1538" y="572" width="6" height="22" fill="#f3e3c2" />
        {/* Wax drip */}
        <path d="M 1538 588 L 1538 593 L 1540 595 L 1540 590 Z" fill="#e6d6c0" opacity="0.85" />
        <rect x="1532" y="592" width="18" height="4" fill="#a87a3a" />
        <ellipse cx="1541" cy="594" rx="10" ry="2" fill="#3a2410" />
        <ellipse cx="1541" cy="568" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
          <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.1s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="1541" cy="569" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
        <ellipse cx="1541" cy="568" rx="22" ry="22" fill="url(#tvSconceHalo)" />
      </g>

      {/* HANGING LANTERNS (4 across the top) — brass cage with cross-
       *  pane glass, chain with visible links, swaying glow inside. */}
      {[
        { x: 240, y: 200, dur: 1.2, depth: 30 },
        { x: 560, y: 180, dur: 1.7, depth: 26 },
        { x: 1040, y: 180, dur: 1.5, depth: 26 },
        { x: 1360, y: 200, dur: 2.1, depth: 30 },
      ].map((l, i) => {
        const chainY1 = 0;
        const chainY2 = l.y - 36;
        const chainLinks = Math.floor((chainY2 - chainY1) / 12);
        return (
          <g key={`tv-lan-${i}`} className="tv-lantern">
            {/* Chain (visible links) */}
            <line x1={l.x} y1={chainY1} x2={l.x} y2={chainY2} stroke="#1a0e08" strokeWidth="1.5" />
            {Array.from({ length: chainLinks }).map((_, j) => (
              <ellipse key={`lc-${i}-${j}`} cx={l.x} cy={chainY1 + 6 + j * 12} rx="2.4" ry="3" fill="none" stroke="#3a2410" strokeWidth="0.8" />
            ))}
            {/* Top cap (brass dome) */}
            <ellipse cx={l.x} cy={l.y - 32} rx="14" ry="4" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
            <rect x={l.x - 14} y={l.y - 32} width="28" height="6" fill="#3a2410" stroke="#1a0e08" strokeWidth="0.8" />
            {/* Cage body (rounded rectangle) */}
            <ellipse cx={l.x} cy={l.y} rx="22" ry={l.depth} fill="#1a0e08" stroke="#3a2410" strokeWidth="2" />
            {/* Top hardware band */}
            <rect x={l.x - 16} y={l.y - 28} width="32" height="6" fill="#3a2410" stroke="#0a0608" strokeWidth="0.8" />
            {/* Bottom hardware band */}
            <rect x={l.x - 10} y={l.y + 24} width="20" height="4" fill="#3a2410" stroke="#0a0608" strokeWidth="0.6" />
            {/* Cage bars — vertical (3) + horizontal (2) creating a cross-pane */}
            <line x1={l.x - 11} y1={l.y - 22} x2={l.x - 11} y2={l.y + 22} stroke="#3a2410" strokeWidth="1.5" opacity="0.85" />
            <line x1={l.x}      y1={l.y - 24} x2={l.x}      y2={l.y + 24} stroke="#3a2410" strokeWidth="1.5" opacity="0.85" />
            <line x1={l.x + 11} y1={l.y - 22} x2={l.x + 11} y2={l.y + 22} stroke="#3a2410" strokeWidth="1.5" opacity="0.85" />
            <line x1={l.x - 18} y1={l.y - 6}  x2={l.x + 18} y2={l.y - 6}  stroke="#3a2410" strokeWidth="1" opacity="0.7" />
            <line x1={l.x - 18} y1={l.y + 8}  x2={l.x + 18} y2={l.y + 8}  stroke="#3a2410" strokeWidth="1" opacity="0.7" />
            {/* Glow inside the cage */}
            <ellipse cx={l.x} cy={l.y} rx="14" ry={l.depth - 8} fill="#ff8c42" opacity="0.65">
              <animate attributeName="opacity" values="0.5;0.95;0.65" dur={`${l.dur}s`} repeatCount="indefinite" />
            </ellipse>
            <ellipse cx={l.x} cy={l.y + 2} rx="8" ry={l.depth - 14} fill="#fff7c2" opacity="0.9" />
            {/* Outer halo */}
            <ellipse cx={l.x} cy={l.y} rx="80" ry="80" fill="url(#tvLanternHalo)" />
          </g>
        );
      })}

      {/* WALL SCONCES (4 along the back wall, between hearth and edges) */}
      {[
        { x: 200, dur: 1.4 },
        { x: 600, dur: 1.5 },
        { x: 1000, dur: 1.3 },
        { x: 1200, dur: 1.6 },
      ].map((s, i) => (
        <g key={`tv-sc-${i}`} className="tv-sconce">
          <rect x={s.x - 10} y="384" width="20" height="8" fill="#1a0e08" />
          <rect x={s.x - 4}  y="392" width="8"  height="14" fill="#1a0e08" />
          <rect x={s.x - 6}  y="406" width="12" height="6" fill="#3a2410" />
          <rect x={s.x - 3}  y="372" width="6"  height="14" fill="#f3e3c2" />
          <ellipse cx={s.x} cy="372" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
            <animate attributeName="opacity" values="0.55;0.95;0.7" dur={`${s.dur}s`} repeatCount="indefinite" />
            <animate attributeName="ry" values="6;7.5;5.5;6" dur={`${s.dur}s`} repeatCount="indefinite" />
          </ellipse>
          <ellipse cx={s.x} cy="374" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
          <ellipse cx={s.x} cy="374" rx="40" ry="40" fill="url(#tvSconceHalo)" />
        </g>
      ))}

      {/* DECOR — pendulum clock + framed picture + dartboard + deer skull */}
      <g className="tv-decor">
        {/* Pendulum clock — left of hearth */}
        <rect x="80" y="448" width="56" height="120" fill="#3a2410" stroke="#1a0e08" strokeWidth="2" />
        <circle cx="108" cy="478" r="22" fill="#f3e3c2" stroke="#1a0e08" strokeWidth="2" />
        <circle cx="108" cy="478" r="20" fill="none" stroke="#a87a3a" strokeWidth="1" />
        <line x1="108" y1="478" x2="108" y2="464" stroke="#1a0e08" strokeWidth="2" />
        <line x1="108" y1="478" x2="118" y2="478" stroke="#1a0e08" strokeWidth="1.5" />
        <rect x="94" y="510" width="28" height="48" fill="#0a0604" />
        <g transform="translate(108 510)">
          <line x1="0" y1="0" x2="0" y2="36" stroke="#3a2410" strokeWidth="1.5" />
          <circle cx="0" cy="42" r="6" fill="#a87a3a" stroke="#1a0e08" strokeWidth="1" />
          <animateTransform attributeName="transform" type="rotate" values="-8;8;-8" dur="2s" additive="sum" repeatCount="indefinite" />
        </g>

        {/* Framed picture — between hearth and bar */}
        <rect x="1100" y="220" width="68" height="84" fill="url(#tvOak)" stroke="#1a0e08" strokeWidth="2" />
        <rect x="1108" y="228" width="52" height="68" fill="#3a2a40" />
        <ellipse cx="1134" cy="266" rx="14" ry="14" fill="#1a1218" />

        {/* Dartboard — right of bar / on back wall */}
        <circle cx="1480" cy="240" r="34" fill="#1a1218" stroke="#3a2410" strokeWidth="2" />
        <circle cx="1480" cy="240" r="28" fill="#7a1828" />
        <circle cx="1480" cy="240" r="22" fill="#cab8a0" />
        <circle cx="1480" cy="240" r="14" fill="#7a1828" />
        <circle cx="1480" cy="240" r="6"  fill="#cab8a0" />
        <circle cx="1480" cy="240" r="2"  fill="#1a0e08" />
        {/* Spokes */}
        {[0, 30, 60, 90, 120, 150].map((deg) => (
          <line key={deg} x1="1480" y1="240" x2={1480 + 28 * Math.cos((deg * Math.PI) / 180)} y2={240 + 28 * Math.sin((deg * Math.PI) / 180)}
            stroke="#1a0e08" strokeWidth="0.8" opacity="0.4" />
        ))}

        {/* Deer skull above hearth */}
        <ellipse cx="800" cy="226" rx="34" ry="26" fill="#e6d6c0" stroke="#1a0e08" strokeWidth="1.5" />
        <ellipse cx="786" cy="222" rx="4" ry="6" fill="#1a0e08" />
        <ellipse cx="814" cy="222" rx="4" ry="6" fill="#1a0e08" />
        <path d="M 774 218 Q 750 180 740 160 M 748 174 L 732 168" fill="none" stroke="#e6d6c0" strokeWidth="2.5" />
        <path d="M 826 218 Q 850 180 860 160 M 852 174 L 868 168" fill="none" stroke="#e6d6c0" strokeWidth="2.5" />

        {/* Mid-distance stools (small silhouettes around the room) */}
        {[
          { x: 360, y: 700 }, { x: 460, y: 720 },
          { x: 1180, y: 720 }, { x: 1080, y: 700 },
        ].map((s, i) => (
          <g key={`tv-stool-${i}`}>
            <ellipse cx={s.x} cy={s.y} rx="22" ry="6" fill="#1a0e08" opacity="0.6" />
            <rect x={s.x - 16} y={s.y - 30} width="32" height="6" fill="#3a2410" />
            <rect x={s.x - 14} y={s.y - 24} width="3" height="22" fill="#1a0e08" />
            <rect x={s.x + 11} y={s.y - 24} width="3" height="22" fill="#1a0e08" />
          </g>
        ))}
      </g>

      {/* COBWEBS — upper-left + upper-right corners (atmospheric
       *  detail; very low opacity so they don't compete with content). */}
      <g className="tv-cobwebs" opacity="0.32" stroke="#9a8a72" strokeWidth="0.6" fill="none">
        {/* Upper-left web */}
        <path d="M 0 0 L 80 60" />
        <path d="M 0 30 L 80 60" />
        <path d="M 0 60 L 80 60" />
        <path d="M 40 0 L 80 60" />
        <path d="M 70 0 L 80 60" />
        {/* Radial threads */}
        <path d="M 14 18 Q 38 28 70 16" />
        <path d="M 8 38 Q 36 44 70 38" />
        <path d="M 6 56 Q 38 60 76 58" />
        {/* Upper-right web */}
        <path d="M 1600 0 L 1520 60" />
        <path d="M 1600 30 L 1520 60" />
        <path d="M 1600 60 L 1520 60" />
        <path d="M 1560 0 L 1520 60" />
        <path d="M 1530 0 L 1520 60" />
        <path d="M 1586 18 Q 1562 28 1530 16" />
        <path d="M 1592 38 Q 1564 44 1530 38" />
        <path d="M 1594 56 Q 1562 60 1524 58" />
      </g>

      {/* SOFT LIGHT BEAMS — diagonal warm streaks dropping from each
       *  lantern. Layered under the top fog so they fade naturally. */}
      <g className="tv-beams" opacity="0.06">
        <path d="M 240 220 L 200 900 L 280 900 L 240 220 Z" fill="#ffb27a" />
        <path d="M 560 200 L 530 900 L 590 900 L 560 200 Z" fill="#ffb27a" />
        <path d="M 1040 200 L 1010 900 L 1070 900 L 1040 200 Z" fill="#ffb27a" />
        <path d="M 1360 220 L 1320 900 L 1400 900 L 1360 220 Z" fill="#ffb27a" />
      </g>

      {/* Vignette — soft darkening at horizontal edges + bottom corners
       *  to focus attention on the central hearth + UI overlay. */}
      <radialGradient id="tvVignette" cx="0.5" cy="0.4" r="0.85">
        <stop offset="55%" stopColor="#000000" stopOpacity="0" />
        <stop offset="100%" stopColor="#000000" stopOpacity="0.55" />
      </radialGradient>
      <rect x="0" y="0" width="1600" height="900" fill="url(#tvVignette)" />

      {/* TOP FOG */}
      <rect x="0" y="0" width="1600" height="240" fill="url(#tvTopFog)" />
    </svg>
  );
}

type NpcKind = 'keeper' | 'cat' | 'courier' | 'spirit';

function NpcWalker({ kind }: { kind: NpcKind }) {
  return (
    <div className={`tavern-npc tavern-npc-${kind}`}>
      <span className="tavern-npc-glyph">{npcGlyph(kind)}</span>
    </div>
  );
}
function npcGlyph(kind: NpcKind): string {
  switch (kind) {
    case 'keeper':  return '🍻';
    case 'cat':     return '🐈';
    case 'courier': return '✉';
    case 'spirit':  return '✦';
  }
}

function timeOfDay(): 'dusk' | 'night' | 'late' {
  const t = (Date.now() % TIME_CYCLE_MS) / TIME_CYCLE_MS;
  if (t < 0.33) return 'dusk';
  if (t < 0.66) return 'night';
  return 'late';
}
